import { Button, Input, Popconfirm } from 'antd'
import React, {
  FunctionComponent,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useUpdateZustavateNote } from '../../utils/useUpdateZustavateNote'
import { ZustavateTableItem } from '../../utils/useZustavateData'

const { TextArea } = Input

export interface ZustavateNoteEditProps {
  record: ZustavateTableItem
  onUpdate?: () => void
}

export const ZustavateNoteEdit: FunctionComponent<ZustavateNoteEditProps> =
  memo(({ record, onUpdate }) => {
    const { id: recordId, note } = record
    const { updateNote, isUpdating } = useUpdateZustavateNote()
    const textAreaRef = useRef<HTMLTextAreaElement>(null)

    const [editedNote, setEditedNote] = useState<string | null>(note)
    const [open, setOpen] = useState(false)

    const handleOpenChange = useCallback((newOpen: boolean) => {
      setOpen(newOpen)
      if (newOpen) {
        setTimeout(() => textAreaRef.current?.focus(), 100)
      }
    }, [])

    const handleConfirm = useCallback(async () => {
      const success = await updateNote(recordId, editedNote)
      if (success) {
        setOpen(false)
        onUpdate?.()
      }
    }, [editedNote, onUpdate, recordId, updateNote])

    const handleCancel = useCallback(() => {
      setEditedNote(note)
      setOpen(false)
    }, [note])

    const buttonProps = useMemo(
      () => ({
        cancel: { size: 'middle' as const },
        ok: {
          loading: isUpdating,
          size: 'middle' as const,
        },
      }),
      [isUpdating]
    )

    return (
      <Popconfirm
        title="Upravit poznámku"
        placement="rightTop"
        autoAdjustOverflow
        description={
          <TextArea
            ref={textAreaRef}
            value={editedNote || ''}
            onChange={e => setEditedNote(e.target.value)}
            placeholder="Zadejte poznámku..."
            autoSize={{ minRows: 3 }}
            className="!w-80 !mb-2"
          />
        }
        open={open}
        icon={null}
        onOpenChange={handleOpenChange}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        okText="Uložit"
        cancelText="Zrušit"
        okButtonProps={buttonProps.ok}
        cancelButtonProps={buttonProps.cancel}
      >
        <Button size="small" type="link" loading={isUpdating}>
          Poznámka
        </Button>
      </Popconfirm>
    )
  })
